import { useState, useRef, useEffect, type ComponentType, type SVGProps, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Send, Sparkles, TrendingUp, Target, Clock, FileText } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { api } from '@/api/api-client';
import { toast } from 'sonner';

type IconType = ComponentType<SVGProps<SVGSVGElement> & { className?: string }>;

interface QuickQuestion {
  id: string;
  icon: IconType;
  text: string;
  color: string;
}

interface CallRecord {
  id?: string;
}

interface ChatMessage {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  isError?: boolean;
}

interface AICampaignChatProps {
  calls: CallRecord[] | null | undefined;
  clientEmail: string;
}

interface AnalyzeCampaignResponse {
  data?: {
    /** What POST /ai/analyze-campaign answers. */
    answer?: string;
  };
}

interface FunctionsApi {
  invoke<T = unknown>(name: string, payload?: unknown): Promise<T>;
}

function getQuickQuestions(t: (key: string) => string): QuickQuestion[] {
  return [
    {
      id: 'objections',
      icon: Target,
      text: t('pages:analytics.aiCampaignChat.quickQuestions.objections'),
      color: 'text-orange-500'
    },
    {
      id: 'pitch',
      icon: TrendingUp,
      text: t('pages:analytics.aiCampaignChat.quickQuestions.pitch'),
      color: 'text-[var(--vf-accent-text)]'
    },
    {
      id: 'timing',
      icon: Clock,
      text: t('pages:analytics.aiCampaignChat.quickQuestions.timing'),
      color: 'text-green-500'
    },
    {
      id: 'summary',
      icon: FileText,
      text: t('pages:analytics.aiCampaignChat.quickQuestions.summary'),
      color: 'text-[var(--vf-accent-text)]'
    }
  ];
}

const AICampaignChat = ({ calls, clientEmail }: AICampaignChatProps) => {
  const { t } = useTranslation(['pages']);
  const QUICK_QUESTIONS = getQuickQuestions(t);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const formatAIResponse = (text: string): ReactNode => {
    if (!text) return '';

    const lines = text.split('\n');
    const formattedLines = lines.map((line, index) => {
      if (line.trim().startsWith('**') && line.trim().endsWith('**')) {
        const content = line.trim().slice(2, -2);
        return (
          <div key={index} className="font-semibold text-foreground mt-3 mb-1">
            {content}
          </div>
        );
      }

      if (line.trim().startsWith('- ') || line.trim().startsWith('• ')) {
        const content = line.trim().slice(2);
        return (
          <div key={index} className="flex gap-2 ml-4 mb-1">
            <span className="text-muted-foreground/60 mt-1">•</span>
            <span className="flex-1">{content}</span>
          </div>
        );
      }

      if (line.trim().match(/^\d+\.\s/)) {
        const content = line.trim();
        return (
          <div key={index} className="ml-4 mb-1">
            {content}
          </div>
        );
      }

      if (line.trim() === '') {
        return <div key={index} className="h-2" />;
      }

      const formattedLine = line;
      const boldRegex = /\*\*(.*?)\*\*/g;
      const parts: ReactNode[] = [];
      let lastIndex = 0;
      let match: RegExpExecArray | null;

      while ((match = boldRegex.exec(line)) !== null) {
        if (match.index > lastIndex) {
          parts.push(
            <span key={`text-${lastIndex}`}>
              {line.substring(lastIndex, match.index)}
            </span>
          );
        }
        parts.push(
          <strong key={`bold-${match.index}`} className="font-semibold text-foreground">
            {match[1]}
          </strong>
        );
        lastIndex = match.index + match[0].length;
      }

      if (lastIndex < line.length) {
        parts.push(<span key={`text-${lastIndex}`}>{line.substring(lastIndex)}</span>);
      }

      if (parts.length > 0) {
        return (
          <div key={index} className="mb-1">
            {parts}
          </div>
        );
      }

      return (
        <div key={index} className="mb-1">
          {formattedLine}
        </div>
      );
    });

    return <div className="space-y-0.5">{formattedLines}</div>;
  };

  const sendQuestion = async (question: string) => {
    if (!question.trim()) return;

    const userMessage: ChatMessage = {
      id: Date.now(),
      role: 'user',
      content: question,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      // The server reads only the first 50 calls and counts the ids it gets;
      // the AI request body is capped at 200 KB (AUDIT-ADM-05, ADM-14), so a
      // large organisation sends its 500 first calls, not all of them.
      const callIds = (calls ?? []).map((c) => c.id).filter((id): id is string => Boolean(id)).slice(0, 500);

      const response = await (api as { functions: FunctionsApi }).functions.invoke<AnalyzeCampaignResponse>(
        'analyzeCampaign',
        {
          clientEmail,
          question: question.trim(),
          callIds,
        },
      );

      const aiMessage: ChatMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        // The route answers { answer } (AUDIT-ADM-12: `analysis` was never sent).
        content: response?.data?.answer || t('pages:analytics.aiCampaignChat.noAnalysis'),
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, aiMessage]);
    } catch (error) {
      console.error('Error analyzing campaign:', error);
      toast.error(t('pages:analytics.aiCampaignChat.analysisErrorTitle'), {
        description: t('pages:analytics.aiCampaignChat.analysisErrorDescription'),
      });

      const errorMessage: ChatMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: t('pages:analytics.aiCampaignChat.errorMessage'),
        timestamp: new Date(),
        isError: true
      };

      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    sendQuestion(inputValue);
  };

  const handleQuickQuestion = (question: string) => {
    sendQuestion(question);
  };

  const callCount = calls?.length || 0;
  const hasMessages = messages.length > 0;

  return (
    <Card className="flex flex-col h-full">
      <CardHeader className="bg-[var(--vf-accent-bg)] border-b">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-[var(--vf-primary)] w-10 h-10 rounded-full flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-lg">{t('pages:analytics.aiCampaignChat.title')}</CardTitle>
              <p className="text-sm text-muted-foreground mt-0.5">
                {t('pages:analytics.aiCampaignChat.subtitle')}
              </p>
            </div>
          </div>
          <Badge variant="secondary" className="flex items-center gap-1.5 px-3 py-1">
            <div className="w-2 h-2 rounded-full bg-green-500" />
            {t('pages:analytics.aiCampaignChat.callsAnalyzed', { count: callCount, plural: callCount !== 1 ? 's' : '' })}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col p-0 overflow-hidden">
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {!hasMessages && (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-[var(--vf-accent-bg)] flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-[var(--vf-accent-text)]" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                {t('pages:analytics.aiCampaignChat.emptyTitle')}
              </h3>
              <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
                {t('pages:analytics.aiCampaignChat.emptyHint')}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl mx-auto">
                {QUICK_QUESTIONS.map((q) => {
                  const IconComponent = q.icon;
                  return (
                    <motion.button
                      key={q.id}
                      onClick={() => handleQuickQuestion(q.text)}
                      className="flex items-center gap-3 p-4 rounded-lg border border-border hover:border-[var(--vf-accent-bd)] hover:bg-[var(--vf-accent-bg)] transition-all text-left group"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <IconComponent className={`w-5 h-5 ${q.color} flex-shrink-0`} />
                      <span className="text-sm font-medium text-foreground/80 group-hover:text-foreground">
                        {q.text}
                      </span>
                    </motion.button>
                  );
                })}
              </div>
            </div>
          )}

          {hasMessages && (
            <AnimatePresence mode="popLayout">
              {messages.map((message) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                      message.role === 'user'
                        ? 'bg-[var(--vf-primary)] text-white'
                        : message.isError
                        ? 'bg-red-50 border border-red-200 text-red-800'
                        : 'bg-muted text-foreground'
                    }`}
                  >
                    {message.role === 'user' ? (
                      <p className="text-sm leading-relaxed">{message.content}</p>
                    ) : (
                      <div className="text-sm leading-relaxed">
                        {formatAIResponse(message.content)}
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          )}

          {isLoading && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex justify-start"
            >
              <div className="max-w-[80%] rounded-2xl px-4 py-3 bg-muted">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Sparkles className="w-4 h-4 animate-pulse" />
                  <span className="text-sm">{t('pages:analytics.aiCampaignChat.analyzing')}</span>
                  <span className="flex gap-1">
                    <motion.span
                      animate={{ opacity: [0.3, 1, 0.3] }}
                      transition={{ duration: 1.2, repeat: Infinity, delay: 0 }}
                    >
                      .
                    </motion.span>
                    <motion.span
                      animate={{ opacity: [0.3, 1, 0.3] }}
                      transition={{ duration: 1.2, repeat: Infinity, delay: 0.2 }}
                    >
                      .
                    </motion.span>
                    <motion.span
                      animate={{ opacity: [0.3, 1, 0.3] }}
                      transition={{ duration: 1.2, repeat: Infinity, delay: 0.4 }}
                    >
                      .
                    </motion.span>
                  </span>
                </div>
              </div>
            </motion.div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {hasMessages && QUICK_QUESTIONS.length > 0 && (
          <div className="border-t px-6 py-3 surface-1">
            <div className="flex flex-wrap gap-2">
              {QUICK_QUESTIONS.map((q) => (
                <Button
                  key={q.id}
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickQuestion(q.text)}
                  disabled={isLoading}
                  className="text-xs hover:bg-[var(--vf-accent-bg)] hover:border-[var(--vf-accent-bd)]"
                >
                  {q.text}
                </Button>
              ))}
            </div>
          </div>
        )}

        <div className="border-t p-4 bg-card">
          <form onSubmit={handleSubmit} className="flex gap-2">
            <Input
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={t('pages:analytics.aiCampaignChat.inputPlaceholder')}
              disabled={isLoading}
              className="flex-1"
            />
            <Button
              type="submit"
              disabled={isLoading || !inputValue.trim()}
              size="icon"
              className="bg-[var(--vf-primary)] hover:bg-[var(--vf-primary-hover)]"
            >
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </div>
      </CardContent>
    </Card>
  );
};

export default AICampaignChat;
